import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";


import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Fade,
  IconButton,
  Stack,
  Typography
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useCallback, useContext, useEffect, useRef, useState, useMemo } from "react";
import * as wellknown from "wellknown";
import { useConfig } from "../../contexts/ConfigContext";
import { useOptions } from "../../contexts/OptionsContext";
import { SelectionContext } from "../../contexts/SelectionContext";
import { useSpinner } from "../../contexts/SpinnerContext";
import { useUser } from "../../contexts/UserContext";
import {
  ApiEndpoint,
  EntityItem,
  MediaItem,
  MediaItemDetailed,
  MediaResponse,
  RelatedEntityItem,
  RelationshipsResponse
} from "../../types";
import { FILE_EXTENSIONS } from "../../utilities/mediaTypes";
import { constructMediaUrl, getSafeThumbnailUrl, getThumbnailIcon, isPdfUrl, isPreviewable, isVideoUrl } from "../../utilities/mediaUtils";
import { isImageUrl, getDataItemName, getDataItemDescription, getDataItemPrimaryImage, getRelatedMedia, getDataSourceType, openItemForEditing } from "../../utilities/dataSourceAdapters";
import { idsEqual } from "../../utilities/idUtils";
import { constructThumbnailUrl } from "../../utilities/temporaryUrlUtils";
import { CMChip } from "../common/CMChip";
import ErrorBoundary from "../common/ErrorBoundary";
import { FulcrumImage } from "../common/FulcrumImage";
import FlashyButton from "../common/FlashyButton";
import ImageCarousel from "../common/ImageCarousel";
import MediaViewer from "../media/MediaViewer";
import ItemFormDialog from "./ItemFormDialog";
import RelatedItemsRing from "./RelatedItemsRing";
import { getEntityColor } from "../../utilities/dataItemUtils";
import AdditionalDetailsPanel from "./AdditionalDetailsPanel";
// Adapter Pattern support for external data sources
import { useIsEndpointEditable } from "../../hooks/useDataSourceAdapter";

// Add debug prefix for DataItemDetails debugging
const DEBUG_PREFIX = '[🖼️ DETAILS-DEBUG]';

// UI Constants for maintainability
const UI_CONSTANTS = {
  BUTTON_SPACER_HEIGHT: '50px',
  CARD_BORDER_RADIUS: '12px 12px 0 0',
  SEPARATOR_BORDER: '1px solid rgba(0, 0, 0, 0.08)',
  GLASS_BACKGROUND: {
    POPUP: 'rgba(255, 255, 255, 0.98)',
    NORMAL: 'rgba(255, 255, 255, 0.95)'
  }
} as const;

/**
 * Helper function to get consistent background styling based on popup mode
 * Follows DRY principle by centralizing background logic
 * @param popupMode - Whether the component is in popup mode (defaults to false if undefined)
 * @returns The appropriate background CSS value
 */
const getCardBackground = (popupMode?: boolean): string => {
  return popupMode ? UI_CONSTANTS.GLASS_BACKGROUND.POPUP : UI_CONSTANTS.GLASS_BACKGROUND.NORMAL;
};

interface DataItemDetailsProps {
  item: MediaItem | EntityItem;
  onBack: () => void;
  dataDrawerEndpoint: ApiEndpoint | null;
  dataItemEndpoint: ApiEndpoint | null;
  onItemUpdate?: (updatedItem: MediaItem | EntityItem) => void;
  onItemDelete?: (itemId: number | string) => void;
  onOpenBulkEdit?: (itemIds: string[]) => void;
  isEditingDisabled?: boolean;
  availableHeight?: string;
  popupMode?: boolean;
  onShowRelatedItems?: () => void; // Callback to trigger related items mode
  hideRelatedItemsRing?: boolean; // General prop to hide RelatedItemsRing for various use cases
}

// Update type guard
const isMediaItem = (item: any): item is MediaItem => {
  return "url" in item && "thumbnail" in item && "media_type" in item;
};

const DataItemDetails: React.FC<DataItemDetailsProps> = React.memo(({
  item: initialItem,
  onBack,
  dataDrawerEndpoint,
  dataItemEndpoint,
  onItemUpdate,
  onItemDelete,
  onOpenBulkEdit,
  isEditingDisabled,
  availableHeight,
  popupMode,
  onShowRelatedItems,
  hideRelatedItemsRing,
}): JSX.Element => {
  
  // PERFORMANCE MONITORING: Track prop changes to identify re-render causes
  const propChangesRef = useRef<any>({
    initialItem: initialItem,
    onBack: onBack,
    dataDrawerEndpoint: dataDrawerEndpoint,
    dataItemEndpoint: dataItemEndpoint,
    onItemUpdate: onItemUpdate,
    onItemDelete: onItemDelete,
    onOpenBulkEdit: onOpenBulkEdit,
    isEditingDisabled: isEditingDisabled,
    availableHeight: availableHeight,
    popupMode: popupMode,
  });
  
  useEffect(() => {
    const prevProps = propChangesRef.current;
    const currentProps = {
      initialItem: initialItem,
      onBack: onBack,
      dataDrawerEndpoint: dataDrawerEndpoint,
      dataItemEndpoint: dataItemEndpoint,
      onItemUpdate: onItemUpdate,
      onItemDelete: onItemDelete,
      onOpenBulkEdit: onOpenBulkEdit,
      isEditingDisabled: isEditingDisabled,
      availableHeight: availableHeight,
      popupMode: popupMode,
    };
    
    const changedProps: string[] = [];
    (Object.keys(currentProps) as Array<keyof typeof currentProps>).forEach((key) => {
      if (prevProps[key] !== currentProps[key]) {
        changedProps.push(key);
      }
    });
    
    if (changedProps.length > 0) {
      console.log(`🔍 [DataItemDetails] PERF: Props changed:`, {
        changedProps: changedProps,
        itemId: initialItem?.id,
        itemName: initialItem?.name,
        prevAvailableHeight: prevProps.availableHeight,
        currentAvailableHeight: availableHeight,
        functionChanges: {
          onBack: prevProps.onBack !== onBack,
          onItemUpdate: prevProps.onItemUpdate !== onItemUpdate,
          onItemDelete: prevProps.onItemDelete !== onItemDelete,
          onOpenBulkEdit: prevProps.onOpenBulkEdit !== onOpenBulkEdit,
        }
      });
    }
    
    propChangesRef.current = currentProps;
  });
  

  
  const config = useConfig();
  const { enqueueSnackbar } = useSnackbar();
  const { hasWritePermissions } = useUser();
  const [item, setItem] = useState(initialItem);
  const [activeEndpoint, setActiveEndpoint] = useState<ApiEndpoint | null>(dataItemEndpoint);
  const [loadingRels, setLoadingRels] = useState(false);
  const [relatedMedia, setRelatedMedia] = useState<MediaResponse[]>([]);
  const { options } = useOptions();
  
  // Edit dialog state (only dialog we keep)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  
  // Preview states
  const [previewItem, setPreviewItem] = useState<MediaResponse | MediaItemDetailed | null>(null);
  const { loading, startLoading, stopLoading } = useSpinner();
  const [isChangingItem, setIsChangingItem] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [thumbnailError, setThumbnailError] = useState(false);
  const [fullImageError, setFullImageError] = useState(false);


  // const latestRequestRef = useRef<number | null>(null); // unused
  const ringContainerRef = useRef<HTMLDivElement>(null);
  const [ringContainerSize, setRingContainerSize] = useState<{width: number, height: number}>({ width: 0, height: 0 });

  
  // Get SelectionContext - the key addition for centralized selection
  const { selectedEntity, setSelectedEntity, selectedEndpoint, refreshSelectedEntity, loadingProgress, fetchRelationshipsForSelected } = useContext(SelectionContext);

  // Helper function to get thumbnail URL for relationship media items
  const getRelationshipMediaThumbnailUrl = (mediaItem: any): string => {
    // Use the proper thumbnail URL utilities from temporaryUrlUtils.ts
    // This ensures consistency with other components and follows the proper fallback hierarchy
    const thumbnailUrl = constructThumbnailUrl(mediaItem);
    
    console.log('[DataItemDetails] Constructing thumbnail URL:', {
      mediaItemId: mediaItem.id,
      mediaItemName: mediaItem.name,
      hasEntityThumbnail: !!mediaItem.entity_thumbnail,
      hasThumbnail: !!mediaItem.thumbnail,
      hasUrl: !!mediaItem.url,
      constructedUrl: thumbnailUrl
    });
    
    // If that fails, fall back to API endpoint
    if (!thumbnailUrl && mediaItem.id) {
      const fallbackUrl = constructMediaUrl(`/api/user/media/${mediaItem.id}/thumbnail/`);
      console.log('[DataItemDetails] Using fallback thumbnail URL:', fallbackUrl);
      return fallbackUrl;
    }
    
    return thumbnailUrl || '';
  };

  // Add debug logging for context and prop changes
  useEffect(() => {
    console.log('[DataItemDetails] Props/Context changed:', {
      initialItemId: initialItem?.id,
      initialItemName: initialItem?.name,
      currentItemId: item?.id,
      currentItemName: item?.name,
      activeEndpointName: activeEndpoint?.name,
      activeEndpointTableName: activeEndpoint?.tableName,
      selectedEntityId: selectedEntity?.id,
      selectedEntityName: selectedEntity?.name,
      selectedEntityType: selectedEntity?.entityType,
      selectedEntityIsComplete: selectedEntity?.isComplete,
      popupMode
    });
  }, [initialItem, item, activeEndpoint, selectedEntity, popupMode]);

  // Check if this is a Fulcrum photo that shouldn't be displayed as standalone
  useEffect(() => {
    if (initialItem) {
      const itemData = { ...initialItem, ...(initialItem as any).properties };
      const dataSourceType = getDataSourceType(itemData, dataItemEndpoint);
      
      const isFulcrumPhoto = !!(
        (itemData as any).fulcrum_photo?.photo_id || 
        (itemData as any).fulcrum_data?.form_id || 
        (itemData as any)._source?.type === 'fulcrum' ||
        dataSourceType === 'fulcrum'
      );
      
      const isMediaItem = (initialItem as any).media_type || 
                         (initialItem as any).entity_thumbnail || 
                         dataItemEndpoint?.tableName === 'media';
      
      if (isMediaItem && isFulcrumPhoto) {
        console.log('[DataItemDetails] Fulcrum photo detected - redirecting back to list');
        enqueueSnackbar('This item does not have its own properties', { 
          variant: 'info' 
        });
        onBack();
        return;
      }
    }
  }, [initialItem, dataItemEndpoint, onBack, enqueueSnackbar]);

  // Simplified: Only sync when we get a different item from props
  useEffect(() => {
    if (initialItem && (!item || item.id !== initialItem.id)) {
      setItem(initialItem);
      setActiveEndpoint(dataItemEndpoint);
    }
  }, [initialItem, dataItemEndpoint, item?.id]);

  // CRITICAL: Update activeEndpoint when dataItemEndpoint changes
  useEffect(() => {
    if (dataItemEndpoint && dataItemEndpoint.tableName !== activeEndpoint?.tableName) {
      setActiveEndpoint(dataItemEndpoint);
    }
  }, [dataItemEndpoint, activeEndpoint, item?.id, item?.name]);

  // Get image and thumbnail URLs using generalized functions
  const primaryImageUrl = getDataItemPrimaryImage(item);
  const imageUrl = primaryImageUrl && isImageUrl(primaryImageUrl) ? constructMediaUrl(primaryImageUrl) : null;

  // Enhanced thumbnail detection using adapter pattern
  let enhancedThumbnailUrl = getSafeThumbnailUrl(item, dataItemEndpoint);
  
  // Use fallback thumbnail sources for all data sources via generic adapter functions
  if (!enhancedThumbnailUrl) {
    // Use generic adapter function to get primary image
    const primaryImageUrl = getDataItemPrimaryImage(item);
    if (primaryImageUrl) {
      enhancedThumbnailUrl = primaryImageUrl;
    }
    
    // Check item.thumbnail (set by adapters for various data sources)
    if (!enhancedThumbnailUrl && (item as any)?.thumbnail) {
      enhancedThumbnailUrl = (item as any).thumbnail;
    }
    
    // Check primary_image.thumbnail as secondary source
    if (!enhancedThumbnailUrl && (item as any)?.primary_image?.thumbnail) {
      enhancedThumbnailUrl = (item as any).primary_image.thumbnail;
    }
  }
  
  const thumbnailUrl = enhancedThumbnailUrl;
  
  // For non-image files, we should show the fallback only if no thumbnail is available
  // const isNonImageFile = isMediaItem(item) && item.url && (isVideoUrl(item.url) || isPdfUrl(item.url) || !isImageUrl(item.url));
  
  // Determine if we should show any media area at all
  const shouldShowMediaArea = (thumbnailUrl && !thumbnailError) || (imageUrl && !fullImageError);
  
  // Show fallback only for media items that have a URL but no thumbnail/image
  const shouldShowFallback = isMediaItem(item) && item.url && isPreviewable(item.url) && !shouldShowMediaArea;

  // Helper function to render the media area
  const renderMediaArea = () => {
    console.log(`${DEBUG_PREFIX} Rendering media area:`, {
      shouldShowMediaArea,
      shouldShowFallback,
      thumbnailUrl,
      imageUrl
    });
    
    // Show a small white area when no media to prevent button overlay on text
    if (!shouldShowMediaArea && !shouldShowFallback) {
      console.log(`${DEBUG_PREFIX} No media area to show - rendering button spacer`);
      return (
        <Box
          sx={{
            width: '100%',
            height: UI_CONSTANTS.BUTTON_SPACER_HEIGHT,
            background: getCardBackground(popupMode),
            borderRadius: UI_CONSTANTS.CARD_BORDER_RADIUS,
            position: 'relative',
            borderBottom: UI_CONSTANTS.SEPARATOR_BORDER,
          }}
        />
      );
    }

    // Helper function to create the click to view banner for media items
    const renderClickToViewBanner = () => {
      if (!isMediaItem(item) || !item.url || !isPreviewable(item.url)) return null;
      
      return (
        <Box
          sx={{
            position: 'absolute',
            bottom: 12,
            left: 12,
            right: 12,
            
            // Enhanced glass morphism banner
            background: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            
            px: 3,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            zIndex: 2,
            
            // Enhanced shadow and transitions
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            transition: 'all 0.2s ease-in-out',
            
            '&:hover': {
              background: 'rgba(0, 0, 0, 0.85)',
              transform: 'translateY(-1px)',
              boxShadow: '0 6px 25px rgba(0, 0, 0, 0.4)',
            }
          }}
        >
          <VisibilityIcon 
            sx={{ 
              fontSize: 18,
              color: 'white',
              filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))'
            }} 
          />
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 600,
              color: 'white',
              fontSize: '0.9rem',
              letterSpacing: '0.02em',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
            }}
          >
            Click to View
          </Typography>
        </Box>
      );
    };

    if (shouldShowFallback) {
      console.log(`${DEBUG_PREFIX} Showing fallback media area`);
      return (
        <Box
          sx={{
            position: 'relative',
            height: "140px", // Slightly taller
            width: "100%", 
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            
            // Modern gradient background
            background: `linear-gradient(135deg, 
              ${getEntityColor((selectedEntity?.entityType as 'media' | 'related' | 'geo') || 'related')}10 0%, 
              ${getEntityColor((selectedEntity?.entityType as 'media' | 'related' | 'geo') || 'related')}05 100%)`,
            
            // Enhanced border
            border: `2px dashed ${getEntityColor((selectedEntity?.entityType as 'media' | 'related' | 'geo') || 'related')}60`,
            borderRadius: 2,
            
            cursor: "pointer",
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            
            '&:hover': {
              background: `linear-gradient(135deg, 
                ${getEntityColor((selectedEntity?.entityType as 'media' | 'related' | 'geo') || 'related')}20 0%, 
                ${getEntityColor((selectedEntity?.entityType as 'media' | 'related' | 'geo') || 'related')}10 100%)`,
              borderColor: getEntityColor((selectedEntity?.entityType as 'media' | 'related' | 'geo') || 'related'),
              transform: 'translateY(-1px)',
              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
            }
          }}
          onClick={() => {
            if (isMediaItem(item) && item.url) {
              setPreviewItem(item as unknown as MediaResponse);
              setPreviewUrl(item.url);
              setIsPreviewOpen(true);
            }
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: `linear-gradient(135deg, 
                ${getEntityColor((selectedEntity?.entityType as 'media' | 'related' | 'geo') || 'related')}20 0%, 
                ${getEntityColor((selectedEntity?.entityType as 'media' | 'related' | 'geo') || 'related')}40 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              transition: 'transform 0.2s ease-in-out',
              '&:hover': {
                transform: 'scale(1.1)',
              }
            }}
          >
            <img
              src={getThumbnailIcon(item.url || '')}
              alt="Media icon"
              style={{
                width: "28px", 
                height: "auto",
                display: "block",
                opacity: 0.8,
                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))'
              }}
            />
          </Box>
          
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.primary', 
              fontWeight: 600,
              textAlign: 'center',
              fontSize: '0.95rem',
              letterSpacing: '0.02em'
            }}
          >
            {(() => {
              const mediaType = getMediaType(item as any);
              switch (mediaType) {
                case 'video':
                  return "Click to view video";
                case 'audio':
                  return "Click to view audio file";
                case 'pdf':
                  return "Click to view PDF document";
                case 'document':
                  return "Click to view document";
                case 'image':
                  return "Click to view image";
                default:
                  return "Click to view file";
              }
            })()}
          </Typography>
        </Box>
      );
    }

    if (thumbnailUrl && !thumbnailError) {
      console.log(`${DEBUG_PREFIX} Showing thumbnail: ${thumbnailUrl}`);
      return (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            cursor: (isMediaItem(item) && getMediaUrl(item)) ? "pointer" : "default",

          }}
          onClick={() => {
            if (isMediaItem(item)) {
              const mediaUrl = getMediaUrl(item);
              if (mediaUrl) {
                setPreviewItem(item as unknown as MediaResponse);
                setPreviewUrl(mediaUrl);
                setIsPreviewOpen(true);
              }
            }
          }}
        >
          <FulcrumImage
            src={thumbnailUrl}
            alt={item.name}
            region="AU"
            thumbnail={true}
            sx={{ 
              width: '100%',
              height: 'auto',
              maxHeight: '300px', // Limit height for better proportions
              display: 'block',
              objectFit: "cover",
              borderRadius: '12px 12px 0 0', // Rounded top corners only
              transition: 'transform 0.3s ease-in-out',
              '&:hover': {
                transform: 'scale(1.02)', // Subtle zoom on hover
              }
            }}
            onError={handleThumbnailError}
          />
          <Box className="click-to-view-banner" sx={{ opacity: 1, transition: 'opacity 0.2s ease-in-out' }}>
            {renderClickToViewBanner()}
          </Box>
        </Box>
      );
    }

    if (imageUrl && !fullImageError) {
      console.log(`${DEBUG_PREFIX} Showing full image: ${imageUrl}`);
      return (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            cursor: (isMediaItem(item) && getMediaUrl(item)) ? "pointer" : "default",

          }}
          onClick={() => {
            if (isMediaItem(item)) {
              const mediaUrl = getMediaUrl(item);
              if (mediaUrl) {
                setPreviewItem(item as unknown as MediaResponse);
                setPreviewUrl(mediaUrl);
                setIsPreviewOpen(true);
              }
            }
          }}
        >
          <FulcrumImage
            src={imageUrl}
            alt={item.name}
            region="AU"
            thumbnail={false}
            sx={{ 
              width: '100%',
              height: 'auto',
              maxHeight: '400px', // Limit height for full images
              display: 'block',
              objectFit: "cover",
              borderRadius: '12px 12px 0 0', // Rounded top corners only
              transition: 'transform 0.3s ease-in-out',
              '&:hover': {
                transform: 'scale(1.02)', // Subtle zoom on hover
              }
            }}
            onError={handleFullImageError}
          />
          <Box className="click-to-view-banner" sx={{ opacity: 1, transition: 'opacity 0.2s ease-in-out' }}>
            {renderClickToViewBanner()}
          </Box>
        </Box>
      );
    }

    // If we reach here, don't show any media area
    console.log(`${DEBUG_PREFIX} No valid media to display`);
    return null;
  };

  useEffect(() => {
    // Function to update size
    const updateSize = () => {
      if (ringContainerRef.current) {
        const padding = 32;
        const width = ringContainerRef.current.clientWidth - padding;
        const height = ringContainerRef.current.clientHeight - padding;
        
        console.log('[DataItemDetails] 📏 Measuring container size:', {
          clientWidth: ringContainerRef.current.clientWidth,
          clientHeight: ringContainerRef.current.clientHeight,
          padding,
          finalWidth: width,
          finalHeight: height
        });
        
        setRingContainerSize({ width, height });
      } else {
        console.log('[DataItemDetails] 📏 Container ref not available for measurement');
      }
    };

    // Initial measurement with delay to allow layout
    const timeoutId = setTimeout(() => {
      updateSize();
    }, 100); // Small delay to allow container to be laid out

    // Measure on window resize
    window.addEventListener('resize', updateSize);
    
    // Cleanup listener and timeout
    return () => {
      window.removeEventListener('resize', updateSize);
      clearTimeout(timeoutId);
    };
  }, []);

  // Additional effect to re-measure when entity becomes complete
  useEffect(() => {
    if (selectedEntity?.isComplete && selectedEntity.relationships) {
      console.log('[DataItemDetails] 📏 Entity became complete, re-measuring container...');
      const updateSize = () => {
        if (ringContainerRef.current) {
          const padding = 32;
          const width = ringContainerRef.current.clientWidth - padding;
          const height = ringContainerRef.current.clientHeight - padding;
          
          console.log('[DataItemDetails] 📏 Re-measuring container size:', {
            clientWidth: ringContainerRef.current.clientWidth,
            clientHeight: ringContainerRef.current.clientHeight,
            finalWidth: width,
            finalHeight: height
          });
          
          setRingContainerSize({ width, height });
        }
      };

      // Small delay to ensure layout is complete
      const timeoutId = setTimeout(updateSize, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [selectedEntity?.isComplete, selectedEntity?.relationships]);

  // Basic common properties that are always excluded
  const baseCommonProps = [
    "id",
    "name",
    "description",
    "primary_image",
    "file",
    "relationships", // Keep this to exclude the relationships object itself
  ];

  // Get relationship property names from options
  const relationshipProps = options
    ? Object.keys(options).map((key) => key.toLowerCase())
    : [];

  // Combine base props with relationship props
  const commonProps = [...baseCommonProps, ...relationshipProps];

  // const additionalProps = Object.entries(item).filter(
  //   ([key, value]) =>
  //     !commonProps.includes(key.toLowerCase()) &&
  //     value !== null &&
  //     value !== undefined
  // );

  // Simplified effect to consume data from SelectionContext
  // Only update if SelectionContext has newer data to prevent overwriting fresh local changes
  useEffect(() => {
    if (selectedEntity && selectedEntity.id === item?.id && selectedEntity.isComplete) {
      
      console.log('🔍 [RING-DEBUG] DataItemDetails consuming data from SelectionContext:', {
        selectedEntityId: selectedEntity.id,
        selectedEntityName: selectedEntity.name,
        hasProperties: !!selectedEntity.properties,
        propertiesKeys: selectedEntity.properties ? Object.keys(selectedEntity.properties) : [],
        currentItemKeys: item ? Object.keys(item) : [],
        sampleProperties: selectedEntity.properties ? Object.fromEntries(
          Object.entries(selectedEntity.properties)
            .filter(([key, value]) => !['relationships', 'fulcrum_data', '_source'].includes(key))
            .slice(0, 5)
        ) : {}
      });
      
      // Update local state with complete data INCLUDING relationships
      if (selectedEntity.properties) {
        // Check if SelectionContext has newer data than our local state
        const selectedEntityTimestamp = new Date(selectedEntity.properties.last_updated_at || 0).getTime();
        const localItemTimestamp = new Date(item?.last_updated_at || 0).getTime();
        const selectionContextIsNewer = selectedEntityTimestamp > localItemTimestamp;
        
        // Only update if SelectionContext has newer data or if we don't have relationships yet
        if (selectionContextIsNewer || !item?.relationships) {
          const itemWithRelationships = {
            ...selectedEntity.properties,
            relationships: selectedEntity.relationships
          } as MediaItem | EntityItem;
          
          console.log('🔍 [RING-DEBUG] DataItemDetails updating item with complete data:', {
            itemId: itemWithRelationships.id,
            itemName: itemWithRelationships.name,
            itemKeys: Object.keys(itemWithRelationships),
            sampleFields: Object.fromEntries(
              Object.entries(itemWithRelationships)
                .filter(([key, value]) => !['relationships', 'fulcrum_data', '_source'].includes(key))
                .slice(0, 5)
            )
          });
          
          setItem(itemWithRelationships);
        } else {
          // Still update relationships if they're missing locally but preserve other fields
          if (!item?.relationships && selectedEntity.relationships) {
            setItem(prev => ({
              ...prev,
              relationships: selectedEntity.relationships
            }));
          }
        }
      }
      
      // Update local media state for display using adapter function
      const mediaItems = getRelatedMedia(selectedEntity);
      if (mediaItems.length > 0) {
        const relatedMediaData = mediaItems.map(media => ({
          ...media,
          media_type: media.type || media.media_type
        }));
        setRelatedMedia(relatedMediaData as any);
      } else {
        setRelatedMedia([]);
      }
    }
  }, [selectedEntity?.isComplete, selectedEntity?.relationships, selectedEntity, item?.id]);



  const renderRelationships = () => {
    if (loadingRels) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (!item.relationships) return null;

    return Object.entries(item.relationships)
      .filter(([_, values]) => Array.isArray(values) && values.length > 0)
      .map(([key, values]) => {
        const displayKey = key
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

        return (
          <Box key={key} sx={{ marginBottom: 2 }}>
            <Typography variant="h6" sx={{ color: "text.secondary", mb: 1 }}>
              {displayKey}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {(values as RelatedEntityItem[]).map((value) => (
                <CMChip
                  key={value.id}
                  label={value.entity_name}
                  colorScheme="primary"
                />
              ))}
            </Box>
          </Box>
        );
      });
  };

  const renderRelatedMedia = () => {
    if (isMediaItem(item)) return null;

    if (loadingRels) {
      return (
        <Box
          sx={{
            mt: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Loading related media...
          </Typography>
        </Box>
      );
    }

    if (relatedMedia.length === 0) return null;

    return (
      <>
        <Divider />
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h5" color="primary">
              Related Media ({relatedMedia.length} items)
            </Typography>
          </Box>

          {/* Images Carousel */}
          {relatedMedia.some((item) => isImageMediaItem(item)) && (
            <Box sx={{ mb: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  Images
                </Typography>
              </Box>
              <ErrorBoundary fallback={<Typography>Unable to load image carousel</Typography>}>
                <ImageCarousel
                  mediaItems={relatedMedia
                    .filter(isImageMediaItem)
                    .map(item => ({
                      id: item.id,
                      name: item.name,
                      entity_name: item.name,
                      entity_thumbnail: getRelationshipMediaThumbnailUrl(item),
                      entity_description: item.description
                    }))
                  }
                  onSelectImage={(item: RelatedEntityItem) => {
                    const matchingMedia = relatedMedia.find(
                      (media) => media.id === item.id
                    );
                    
                    if (matchingMedia) {
                      setPreviewItem(matchingMedia);
                      setPreviewUrl(matchingMedia.url || matchingMedia.thumbnail);
                      setIsPreviewOpen(true);
                    }
                  }}
                  selectedItemId={null}
                  disablePreview={false}
                />
              </ErrorBoundary>
            </Box>
          )}
        </Box>
      </>
    );
  };

  const hasRelationships = (relationships: {
    related_entities?: Record<string, RelatedEntityItem[]>;
    geo_entities?: Record<string, RelatedEntityItem[]>;
    media?: RelatedEntityItem[];
  } | undefined): boolean => {
    console.log(`${DEBUG_PREFIX} hasRelationships called with:`, {
      relationships,
      hasRelationships: !!relationships,
      keysFound: relationships ? Object.keys(relationships) : [],
      relatedEntitiesKeys: relationships?.related_entities ? Object.keys(relationships.related_entities) : [],
      geoEntitiesKeys: relationships?.geo_entities ? Object.keys(relationships.geo_entities) : [],
      mediaLength: Array.isArray(relationships?.media) ? relationships.media.length : 0
    });
    
    if (!relationships) return false;

    // Check related_entities
    if (relationships.related_entities && 
        Object.values(relationships.related_entities).some(arr => 
          Array.isArray(arr) && arr.length > 0
        )) {
      console.log(`${DEBUG_PREFIX} hasRelationships returning true due to related_entities`);
      return true;
    }

    // Check geo_entities
    if (relationships.geo_entities && 
        Object.values(relationships.geo_entities).some(arr => 
          Array.isArray(arr) && arr.length > 0
        )) {
      console.log(`${DEBUG_PREFIX} hasRelationships returning true due to geo_entities`);
      return true;
    }

    // Check media
    if (Array.isArray(relationships.media) && relationships.media.length > 0) {
      console.log(`${DEBUG_PREFIX} hasRelationships returning true due to media`);
      return true;
    }

    console.log(`${DEBUG_PREFIX} hasRelationships returning false - no valid relationships found`);
    return false;
  };

  // Helper function to check if an item has potential relationships that can be fetched
  const hasPotentialRelationships = (item: any): boolean => {
    // For Fulcrum items, check if they have form values that might contain linked records
    if (item?.dataSourceType === 'fulcrum' || item?.fulcrum_data) {
      const formValues = item?.fulcrum_data?.form_values || item?.form_values;
      if (formValues && Object.keys(formValues).length > 0) {
        console.log(`${DEBUG_PREFIX} hasPotentialRelationships returning true - Fulcrum item with form values`);
        return true;
      }
    }
    
    // For Django items, they might have relationships if they exist in the system
    if (item?.dataSourceType === 'django' || (!item?.dataSourceType && !item?.fulcrum_data)) {
      console.log(`${DEBUG_PREFIX} hasPotentialRelationships returning true - Django item`);
      return true;
    }
    
    console.log(`${DEBUG_PREFIX} hasPotentialRelationships returning false`);
    return false;
  };

  // Helper function to map relationship category names to endpoint table names
  const mapCategoryToEntityType = (category: string): string => {
    // Handle slash-separated categories (e.g., "geo_entities/place")
    const categoryPart = category.split("/").pop() || category;
    
    // First, try to find by returnedRelationshipsKey (for standard relationships)
    const matchingEndpoint = config.apiEndpoints.find(
      endpoint => endpoint.returnedRelationshipsKey === categoryPart
    );
    
    if (matchingEndpoint) {
      console.log('[DataItemDetails] Found matching endpoint for category:', {
        category: categoryPart,
        endpointName: matchingEndpoint.name,
        tableName: matchingEndpoint.tableName,
        returnedRelationshipsKey: matchingEndpoint.returnedRelationshipsKey
      });
      return matchingEndpoint.tableName;
    }
    
    // Special handling for reverse relationship categories (e.g., "weed_activities" -> "weeds")
    // Look for reverse relationships that use this category as relationshipKey
    const reverseRelEndpoint = config.apiEndpoints.find(endpoint => 
      endpoint.reverseRelationships?.some(rel => rel.relationshipKey === categoryPart)
    );
    
    if (reverseRelEndpoint?.reverseRelationships) {
      // Find the specific reverse relationship to get the source form info
      const reverseRel = reverseRelEndpoint.reverseRelationships.find(rel => rel.relationshipKey === categoryPart);
      
      if (reverseRel) {
        // Find the endpoint that matches the source form
        const sourceEndpoint = config.apiEndpoints.find(endpoint => endpoint.formId === reverseRel.sourceFormId);
        
        if (sourceEndpoint) {
          console.log('[DataItemDetails] Found reverse relationship endpoint for category:', {
            category: categoryPart,
            sourceEndpointName: sourceEndpoint.name,
            sourceTableName: sourceEndpoint.tableName,
            relationshipKey: reverseRel.relationshipKey
          });
          return sourceEndpoint.tableName;
        }
      }
    }
    
    // Fallback: try to find by tableName matching category
    const fallbackEndpoint = config.apiEndpoints.find(
      endpoint => endpoint.tableName === categoryPart || 
                  endpoint.tableName.toLowerCase() === categoryPart.toLowerCase()
    );
    
    if (fallbackEndpoint) {
      console.log('[DataItemDetails] Found fallback endpoint for category:', {
        category: categoryPart,
        endpointName: fallbackEndpoint.name,
        tableName: fallbackEndpoint.tableName
      });
      return fallbackEndpoint.tableName;
    }
    
    console.warn('[DataItemDetails] No endpoint found for category:', categoryPart);
    return categoryPart;
  };

  // PERFORMANCE: Memoize handleRelatedItemClick to prevent RelatedItemsRing re-renders  
  const handleRelatedItemClick = useCallback((entity: RelatedEntityItem, category: string) => {
    const mappedEntityType = mapCategoryToEntityType(category);
    
    // Find the correct endpoint for this entity type
    const targetEndpoint = config.apiEndpoints.find(
      endpoint => endpoint.tableName === mappedEntityType
    );
    
    // Extract the best available name from the entity
    const entityName = entity.entity_name || entity.name || `${targetEndpoint?.name || category} Item`;
    const entityDescription = entity.entity_description || entity.description || '';
    
    console.log('[DataItemDetails] Related item click - delegating to SelectionContext:', {
      entityId: entity.id,
      entityName: entityName,
      entityDescription: entityDescription,
      category,
      mappedEntityType,
      hasThumbnail: !!entity.entity_thumbnail,
      targetEndpoint: targetEndpoint ? {
        name: targetEndpoint.name,
        tableName: targetEndpoint.tableName,
        api_endpoint: targetEndpoint.api_endpoint
      } : null
    });

    if (!targetEndpoint) {
      console.error('[DataItemDetails] No endpoint found for entity type:', mappedEntityType);
      return;
    }

    setIsChangingItem(true);

    // Create a minimal entity that will trigger SelectionContext to fetch complete data via adapter pattern
    const geoEntity = {
      id: entity.id,
      name: entityName, // Use the extracted name as initial display name
      entityType: mappedEntityType,
      layerId: targetEndpoint.mapLayerId || targetEndpoint.tableName,
      // Mark as incomplete so SelectionContext will fetch full data using the adapter pattern
      isComplete: false,
      properties: { 
        fromDataItemDetails: true, // Mark as coming from DataItemDetails
        // Include minimal relationship metadata for immediate display while loading
        entity_name: entity.entity_name,
        entity_description: entity.entity_description,
        entity_thumbnail: entity.entity_thumbnail,
        description: entityDescription,
        thumbnail: entity.entity_thumbnail, // Map entity_thumbnail to thumbnail for compatibility
        rel_description: entity.rel_description,
        // Include raw relationship data for debugging
        _relationshipData: entity
      }
    };
    
    // Pass the correct endpoint to SelectionContext
    setSelectedEntity(geoEntity, targetEndpoint);
    
    // Clear the changing flag after a short delay to let SelectionContext work
    setTimeout(() => {
      setIsChangingItem(false);
    }, 100);
  }, [config.apiEndpoints, setIsChangingItem, setSelectedEntity, mapCategoryToEntityType]);

  // Helper function to check if item is an image media item
  const isImageMediaItem = (item: any): boolean => {
    return (
      (item.media_type &&
        (item.media_type === "photo" ||
          item.media_type.startsWith("image/"))) ||
      (item.remote_details?.mime &&
        item.remote_details.mime.startsWith("image/") &&
        !item.remote_details.mime.startsWith("application/pdf"))
    );
  };

  const transformToResponseShape = (
    relationships: any
  ): RelationshipsResponse => {
    if (!relationships)
      return {
        related_entities: {},
        geo_entities: {
          place: [],
          familyarea: [],
        },
        media: [],
      };

    // If it's already in the new shape, return as is
    if ("related_entities" in relationships) {
      return relationships as RelationshipsResponse;
    }

    // Transform old format to new
    return {
      related_entities: Object.entries(relationships)
        .filter(([key]) => !["places", "family_areas"].includes(key))
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {}),
      geo_entities: {
        place: relationships.places || [],
        familyarea: relationships.family_areas || [],
      },
      media: relationships.media || [],
    };
  };

  // Helper function to detect media type from item
  const getMediaType = (item: MediaItem | EntityItem): string => {
    // Check for media_type property
    if (isMediaItem(item) && item.media_type) {
      return item.media_type.toLowerCase();
    }
    
    // Helper function to check if URL ends with any extension from a list
    const urlEndsWithAnyExtension = (url: string, extensions: string[]): boolean => {
      const lowerUrl = url.toLowerCase();
      return extensions.some(ext => lowerUrl.endsWith(`.${ext.toLowerCase()}`));
    };
    
    // Handle entity items with primary_image
    if (!isMediaItem(item) && item.primary_image?.url) {
      const url = item.primary_image.url;
      if (url.toLowerCase().endsWith('.pdf')) return 'pdf';
      if (urlEndsWithAnyExtension(url, FILE_EXTENSIONS.VIDEO)) return 'video';
      if (urlEndsWithAnyExtension(url, FILE_EXTENSIONS.IMAGE)) return 'image';
      if (urlEndsWithAnyExtension(url, FILE_EXTENSIONS.AUDIO)) return 'audio';
    }
    
    // Check for URL to determine type
    if (isMediaItem(item) && item.url) {
      const url = item.url;
      if (url.toLowerCase().endsWith('.pdf')) return 'pdf';
      if (urlEndsWithAnyExtension(url, FILE_EXTENSIONS.VIDEO)) return 'video';
      if (urlEndsWithAnyExtension(url, FILE_EXTENSIONS.IMAGE)) return 'image';
      if (urlEndsWithAnyExtension(url, FILE_EXTENSIONS.AUDIO)) return 'audio';
    }
    
    // Check remote details if available
    if (isMediaItem(item) && item.remote_details?.mime) {
      const mimeType = item.remote_details.mime.toLowerCase();
      if (mimeType.includes('pdf')) return 'pdf';
      if (mimeType.includes('video')) return 'video';
      if (mimeType.includes('image')) return 'image';
      if (mimeType.includes('audio')) return 'audio';
    }
    
    return 'unknown';
  };

  const handleThumbnailError = () => {
    setThumbnailError(true);
  };

  const handleFullImageError = () => {
    setFullImageError(true);
  };

  // Get media URL helper
  const getMediaUrl = (item: any): string => {
    if (isMediaItem(item) && item.url) {
      return item.url;
    }
    if (!isMediaItem(item) && item.primary_image?.url) {
      return item.primary_image.url;
    }
    return '';
  };

  const handleClosePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewItem(null);
    setIsPreviewOpen(false);
  };





  // Use adapter pattern to determine if endpoint is editable
  const isEndpointEditable = useIsEndpointEditable(activeEndpoint);

  // Helper function to extract geometry from item with proper type handling
  const extractGeometry = (item: MediaItem | EntityItem): any => {
    const itemWithGeometry = item as any; // Type assertion for geometry access
    
    if (itemWithGeometry.geometry) {
      if (typeof itemWithGeometry.geometry === 'string' && itemWithGeometry.geometry.trim()) {
        try {
          return wellknown.parse(itemWithGeometry.geometry);
        } catch (error) {
          console.error('[SELECTION-CONTEXT] Error parsing geometry WKT:', error);
          return null;
        }
      }
      if (typeof itemWithGeometry.geometry === 'object') {
        return itemWithGeometry.geometry;
      }
    }
    
    if (itemWithGeometry._geometry) {
      if (typeof itemWithGeometry._geometry === 'string' && itemWithGeometry._geometry.trim()) {
        try {
          return wellknown.parse(itemWithGeometry._geometry);
        } catch (error) {
          console.error('[SELECTION-CONTEXT] Error parsing _geometry WKT:', error);
          return null;
        }
      }
      if (typeof itemWithGeometry._geometry === 'object') {
        return itemWithGeometry._geometry;
      }
    }
    
    return null;
  };

  // Note: Removed the effect that was updating SelectionContext from local item state
  // This was causing complete entities to be overwritten with incomplete versions
  // DataItemDetails should only consume from SelectionContext, not overwrite it

  // Cleanup effect removed - no longer needed since SelectionContext handles all data fetching

  return (
    <ErrorBoundary
      resetKeys={[item?.id, activeEndpoint?.tableName].filter(Boolean) as (string | number)[]} // Reset error boundary when item or endpoint changes
      fallback={
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '200px',
            p: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" color="error" gutterBottom>
            Unable to load item details
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            There was an error loading the details for this item. This may be due to slow server response or network issues.
          </Typography>
          <Button variant="outlined" onClick={onBack}>
            Go Back
          </Button>
        </Box>
      }
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: availableHeight || "100vh", // Use availableHeight if provided
          display: "flex",
          justifyContent: "center",
          alignItems: "center", // Keep centered
          p: popupMode ? 1 : 3, // Reduced padding in popup mode
          overflow: "hidden",
          transition: "height 0.3s ease-in-out", // Smooth height transitions
        }}
      >
      {/* Main card with enhanced styling and height limit */}
      <Card
        sx={{
          // Clean modern design without blur (to avoid affecting photos)
          background: getCardBackground(popupMode),
          border: `2px solid ${getEntityColor((selectedEntity?.entityType as 'media' | 'related' | 'geo') || 'related')}40`, // 40 = 25% opacity
          borderRadius: 3, // More rounded corners
          
          // Responsive sizing with height limit
          width: popupMode ? '100%' : 'fit-content',
          minWidth: popupMode ? '100%' : 280, // Increased minimum width
          maxWidth: popupMode ? '100%' : 500, // Increased maximum width
          maxHeight: '90%', // Limit height to 90% of available space
          
          // Enhanced shadows and positioning
          position: "relative",
          zIndex: 1,
          boxShadow: popupMode 
            ? '0 25px 50px -12px rgba(0, 0, 0, 0.25)' 
            : '0 20px 40px -12px rgba(0, 0, 0, 0.15), 0 8px 16px -8px rgba(0, 0, 0, 0.1)',
          
          // Layout and overflow for scrolling
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden', // Hide overflow on card itself
          
          // Enhanced transitions and interactions
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)", // Smooth material design easing
          
          // Hover effects for buttons
          '&:hover .edit-button, &:hover .back-button': {
            opacity: 0.9,
            transform: 'scale(1.05)',
          },
          
          // Subtle hover effect on the card itself
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: popupMode 
              ? '0 32px 64px -12px rgba(0, 0, 0, 0.35)' 
              : '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 12px 24px -8px rgba(0, 0, 0, 0.15)',
          }
        }}
      >
        {/* Loading Overlay - Enhanced glass morphism */} 
        <Fade in={isChangingItem}> 
          <Box 
            sx={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              width: '100%', 
              height: '100%', 
              
              // Enhanced glass morphism overlay
              background: 'rgba(255, 255, 255, 0.85)',
              backdropFilter: 'blur(15px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              zIndex: 10,
              
              // Smooth entrance animation
              animation: 'fadeInBlur 0.3s ease-out',
              '@keyframes fadeInBlur': {
                '0%': {
                  opacity: 0,
                  backdropFilter: 'blur(0px)',
                },
                '100%': {
                  opacity: 1,
                  backdropFilter: 'blur(15px)',
                }
              }
            }}
          >
            <CircularProgress 
              size={48} 
              thickness={3}
              sx={{
                color: getEntityColor((selectedEntity?.entityType as 'media' | 'related' | 'geo') || 'related'),
              }}
            />
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontWeight: 500,
                textAlign: 'center'
              }}
            >
              Loading details...
            </Typography>
          </Box>
        </Fade>

        {/* Back Button - Modern glass morphism design */}
        <IconButton
          className="back-button"
          onClick={onBack}
          sx={{
            position: "absolute",
            top: 16,
            left: 16,
            zIndex: 3,
            
            // Glass morphism design
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '12px',
            
            // Enhanced sizing and spacing
            width: 44,
            height: 44,
            padding: 0,
            
            // Modern shadow
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
            
            // Initial state
            opacity: 0.8,
            transform: 'scale(0.95)',
            
            // Smooth transitions
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            
            "& .MuiSvgIcon-root": {
              fontSize: "22px",
              color: 'text.primary',
            },
            
            "&:hover": {
              background: 'rgba(255, 255, 255, 0.95)',
              transform: 'scale(1.05)',
              opacity: 1,
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
            },
            
            "&:active": {
              transform: 'scale(0.98)',
            }
          }}
        >
          <ArrowBackIcon />
        </IconButton>

        {/* Edit Button - Modern glass morphism design */}
        {!isEditingDisabled && isEndpointEditable && hasWritePermissions() && (
          <IconButton
            className="edit-button"
            onClick={() => {
              const currentEndpoint = selectedEndpoint || activeEndpoint;
              const expectedEntityType = selectedEntity?.entityType;
              
              // Validation: Only open dialog if endpoint matches entity type
              if (expectedEntityType && currentEndpoint?.tableName !== expectedEntityType) {
                return;
              }
              
              // Try data source-specific editing first
              const externalEditingHandled = openItemForEditing(item, currentEndpoint);
              
              // If external editing wasn't handled, use internal dialog
              if (!externalEditingHandled) {
                setIsEditDialogOpen(true);
              }
            }}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 3,
              
              // Glass morphism design
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '12px',
              
              // Enhanced sizing and spacing
              width: 44,
              height: 44,
              padding: 0,
              
              // Modern shadow
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
              
              // Initial state
              opacity: 0.8,
              transform: 'scale(0.95)',
              
              // Smooth transitions
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              
              "& .MuiSvgIcon-root": {
                fontSize: "22px",
                color: 'primary.main',
              },
              
              "&:hover": {
                background: 'rgba(255, 255, 255, 0.95)',
                transform: 'scale(1.05)',
                opacity: 1,
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
                
                "& .MuiSvgIcon-root": {
                  color: 'primary.dark',
                }
              },
              
              "&:active": {
                transform: 'scale(0.98)',
              }
            }}
          >
            <EditIcon />
          </IconButton>
        )}

        {/* Render Media Area using helper function */} 
        {renderMediaArea()}

        {/* Content Section - Enhanced styling with scrolling */}
        <CardContent sx={{ 
          flexGrow: 1, 
          p: 3, // Consistent padding since we always show some media area now
          pt: 2, // Better spacing for buttons - now that we always have a media area
          "&:last-child": { pb: 3 },
          
          // Enhanced background
          background: shouldShowMediaArea || shouldShowFallback 
            ? 'transparent' 
            : 'linear-gradient(145deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
          
          // Enable scrolling when content overflows
          overflow: 'auto',
          
          // Enhanced scrollbar styling
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(0, 0, 0, 0.05)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(0, 0, 0, 0.3)',
            }
          }
        }}>
          <Stack spacing={3}> {/* Increased spacing */}
            {/* View Mode content - editing is now handled by dialog */}
            <>
              {/* Endpoint Source Display */}
              {(dataItemEndpoint || activeEndpoint) && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    textAlign: 'center', 
                    color: 'text.secondary',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    fontSize: '0.7rem',
                    mt: shouldShowMediaArea || shouldShowFallback ? 1 : 3,
                    mb: 1,
                    opacity: 0.8,
                    display: 'block'
                  }}
                >
                  From: {(dataItemEndpoint || activeEndpoint)?.name || 'Unknown Source'}
                </Typography>
              )}
              
              <Typography 
                variant="h4" 
                gutterBottom 
                sx={{ 
                  textAlign: 'center', 
                  mt: (dataItemEndpoint || activeEndpoint) ? 0 : (shouldShowMediaArea || shouldShowFallback ? 2 : 4),
                  fontWeight: 600,
                  letterSpacing: '-0.02em',
                  
                  // Simple black text for readability
                  color: 'text.primary',
                  
                  // Subtle text shadow for depth
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                  
                  // Ensure text is always visible
                  minHeight: '1.2em', // Prevent layout shift
                }}
              >
                {getDataItemName(item, dataItemEndpoint || undefined)}
              </Typography>
              
              <Typography 
                variant="body1" 
                sx={{ 
                  color: "text.secondary", 
                  textAlign: 'center', 
                  maxHeight: "120px", 
                  minHeight: "1.2em", // Prevent layout shift when empty
                  overflowY: "auto",
                  lineHeight: 1.6,
                  fontSize: '1.1rem',
                  
                  // Enhanced scrollbar styling
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'rgba(0, 0, 0, 0.05)',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '3px',
                    '&:hover': {
                      background: 'rgba(0, 0, 0, 0.3)',
                    }
                  },
                  
                  // Ensure text is visible even when empty
                  '&:empty::before': {
                    content: '"No description available"',
                    color: 'text.disabled',
                    fontStyle: 'italic',
                  }
                }}
              > 
                {getDataItemDescription(item, dataItemEndpoint || undefined)}
              </Typography>

              {/* On-demand relationships trigger - using reusable FlashyButton */}
              {selectedEntity && (!selectedEntity.relationships || Object.keys(selectedEntity.relationships || {}).length === 0) && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                  <FlashyButton
                    variant="outlined"
                    size="medium"
                    disabled={loadingRels || loading}
                    onClick={async () => {
                      setLoadingRels(true);
                      startLoading(); // Use SpinnerContext for global loading state
                      
                      try {
                        const found = await fetchRelationshipsForSelected();
                        
                        // Only show "No related items found" if it's not an error case
                        // SelectionContext will handle error messages directly
                        if (!found) {
                          enqueueSnackbar('No related items found', { variant: 'info' });
                        } else {
                          // Automatically switch DataGrid to related items mode when relationships are found
                          onShowRelatedItems?.();
                        }
                      } catch (error) {
                        console.error('[DataItemDetails] Error in Find Related:', error);
                        // Error handling is done in SelectionContext, so we don't need to show additional messages
                      } finally {
                        setLoadingRels(false);
                        stopLoading(); // Always stop the global spinner
                      }
                    }}
                    flashColors={['#ff69b4', '#9966cc']} // Pink to purple
                    borderColor="purple"
                    textColor="black"
                    pulseInterval={2}
                    flashInterval={2}
                    flashOpacity={0.5}
                    flashWidth="80%"
                  >
                    Find Related
                  </FlashyButton>
                </Box>
              )}
              
              {/* Additional Properties Section */}
              <AdditionalDetailsPanel 
                item={item}
                endpoint={dataItemEndpoint || undefined}
              />
            </>
          </Stack>
        </CardContent>
      </Card>

      {/* Related Items Ring/Cloud - Now reactive to SelectionContext, can be hidden via prop */}
      {!popupMode && !hideRelatedItemsRing && selectedEntity && idsEqual(selectedEntity.id, item?.id) && (() => {
        
        return (
          <Box
            ref={ringContainerRef}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%', 
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden', 
              pointerEvents: 'none', // Explicitly allow events to pass through to MediaDrawer
              zIndex: 2, // Higher than main card to ensure ring items are clickable
              transition: "all 0.3s ease-in-out", // Smooth transitions for repositioning
            }}
          >
            {!selectedEntity.isComplete || loadingRels || loading ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'auto', // Loading indicator should be clickable
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: 2,
                  p: 3,
                  boxShadow: 3,
                  minWidth: 200,
                  textAlign: 'center'
                }}
              >
                <CircularProgress size={40} color="primary" />
                <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary' }}>
                  {loadingProgress 
                    ? `${loadingProgress.step} (${loadingProgress.current}/${loadingProgress.total})`
                    : loadingRels 
                    ? 'Finding related items...'
                    : 'Loading relationships...'
                  }
                </Typography>
                {/* Removed inline action here per request; on-demand button exists under title */}
                {loadingProgress && loadingProgress.total > 1 && (
                  <Box sx={{ width: '100%', mt: 1, maxWidth: 200 }}>
                    <Box sx={{ 
                      width: `${(loadingProgress.current / loadingProgress.total) * 100}%`, 
                      height: 4, 
                      backgroundColor: 'primary.main',
                      borderRadius: 2,
                      transition: 'width 0.3s ease-in-out'
                    }} />
                  </Box>
                )}
              </Box>
            ) : (() => {
              const hasActualRelationships = selectedEntity.relationships && hasRelationships(selectedEntity.relationships);
              const hasPotential = hasPotentialRelationships(selectedEntity);
              const shouldRender = ringContainerSize.width > 0 && ringContainerSize.height > 0 && selectedEntity.relationships && (hasActualRelationships || hasPotential);
              console.log('[DataItemDetails] 🎯 RelatedItemsRing shouldRender:', {
                shouldRender,
                hasActualRelationships,
                hasPotential,
                containerReady: ringContainerSize.width > 0 && ringContainerSize.height > 0,
                hasRelationshipsObject: !!selectedEntity.relationships
              });
              
              if (shouldRender) {
                const transformedRelationships = transformToResponseShape(selectedEntity.relationships);
                console.log('[DataItemDetails] 🎯 About to render RelatedItemsRing with:', {
                  containerWidth: ringContainerSize.width,
                  containerHeight: ringContainerSize.height,
                  transformedRelationships
                });
                
                return (
                  <ErrorBoundary>
                    <RelatedItemsRing
                      relationships={transformedRelationships}
                      onItemClick={handleRelatedItemClick}
                      containerWidth={ringContainerSize.width}
                      containerHeight={ringContainerSize.height}
                    />
                  </ErrorBoundary>
                );
              }
              
              console.log('[DataItemDetails] 🚫 RelatedItemsRing NOT rendering due to failed conditions');
              return null;
            })()}
          </Box>
        );
      })()}

      {/* Edit Item Dialog */}
      <ItemFormDialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        endpoint={selectedEndpoint || activeEndpoint} // Use selectedEndpoint from SelectionContext as primary source
        mode="edit"
        existingItem={item}
        onItemUpdated={async (updatedItem) => {
          // Update local state immediately for responsive UI
          setItem(updatedItem);
          
          // Update DataDrawer's data if this is the same endpoint
          if (activeEndpoint === dataDrawerEndpoint && onItemUpdate) {
            onItemUpdate(updatedItem);
          }

          // Refresh SelectionContext to get complete fresh data including relationships and geometry
          // Cache invalidation now happens immediately before response, so no delay needed
          if (selectedEntity && selectedEntity.id === item.id) {
            try {
              await refreshSelectedEntity();
            } catch (error) {
              console.error('[DataItemDetails] Error refreshing entity data after edit:', error);
              // Fallback to manual merge if refresh fails
              const updatedEntity = {
                ...selectedEntity,
                properties: {
                  ...selectedEntity.properties,
                  ...updatedItem
                },
                isComplete: true
              };
              setSelectedEntity(updatedEntity);
            }
          }
        }}
                      onItemDeleted={(deletedItemId: number | string) => {
          // Handle item deletion
          onItemDelete?.(deletedItemId);
          onBack();
        }}
      />
      
      {isPreviewOpen && previewItem && (
        <MediaViewer 
          item={previewItem} 
          open={isPreviewOpen} 
          onClose={handleClosePreview} 
          mode="dialog"
        /> 
      )}
    </Box>
    </ErrorBoundary>
  );
});

export default DataItemDetails;
